/**
 * InputDialog Component
 *
 * A reusable input dialog for prompting user input.
 * Consistent styling matching ConfirmDialog.
 *
 * @module components/common/InputDialog
 */

import { memo, useState, useEffect, useRef } from 'react';
import { FileText, Loader2 } from 'lucide-react';

/**
 * InputDialog Component
 *
 * @param {object} props - Component props
 * @param {boolean} props.isOpen - Whether the dialog is visible
 * @param {string} props.title - Dialog title
 * @param {string} [props.message] - Prompt message
 * @param {string} [props.placeholder] - Input placeholder
 * @param {string} [props.defaultValue=''] - Default input value
 * @param {string} [props.confirmLabel='Create'] - Label for confirm button
 * @param {string} [props.cancelLabel='Cancel'] - Label for cancel button
 * @param {function} props.onConfirm - Confirm callback with input value
 * @param {function} props.onCancel - Cancel callback
 * @param {boolean} [props.isProcessing=false] - Whether action is in progress
 * @param {React.ComponentType} [props.icon] - Optional icon component
 * @returns {React.ReactElement|null} Dialog element or null
 */
const InputDialog = memo(function InputDialog({
  isOpen,
  title,
  message,
  placeholder = '',
  defaultValue = '',
  confirmLabel = 'Create',
  cancelLabel = 'Cancel',
  onConfirm,
  onCancel,
  isProcessing = false,
  icon: Icon = FileText
}) {
  const [value, setValue] = useState(defaultValue);
  const inputRef = useRef(null);

  // Reset value when dialog opens
  useEffect(() => {
    if (isOpen) {
      setValue(defaultValue);
      // Focus input after render
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isOpen, defaultValue]);

  // Handle Enter key
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && value.trim() && !isProcessing) {
      onConfirm(value.trim());
    } else if (e.key === 'Escape') {
      onCancel();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-gray-900 border border-gray-700 rounded-xl shadow-2xl w-full max-w-md mx-4">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-700">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <Icon className="w-5 h-5 text-indigo-400" />
            {title}
          </h3>
        </div>

        {/* Content */}
        <div className="px-6 py-4">
          {message && (
            <p className="text-gray-300 mb-4">
              {message}
            </p>
          )}
          <input
            ref={inputRef}
            type="text"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={isProcessing}
            className="w-full px-4 py-2.5 bg-gray-800 border border-gray-700 rounded-lg text-gray-200 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent disabled:opacity-50"
          />
        </div>

        {/* Actions */}
        <div className="px-6 py-4 border-t border-gray-700 flex justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={isProcessing}
            className="px-4 py-2 text-sm font-medium text-gray-300 hover:text-white hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={() => value.trim() && onConfirm(value.trim())}
            disabled={isProcessing || !value.trim()}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-500 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isProcessing ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Icon className="w-4 h-4" />
            )}
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
});

export default InputDialog;
